import moment from "moment"
import React from "react"
import { Button, Col, Dropdown, OverlayTrigger, Row, Tooltip } from "react-bootstrap"
import { ChevronDown, ChevronUp } from "react-bootstrap-icons"
import { ColumnDescription } from "react-bootstrap-table-next"
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit"
// import BootstrapTable from "react-bootstrap-table-next";
import { useTranslation } from "react-i18next"
import { CustomerListModel } from "../../api/customer.api"
import CustomTotal from "../../components/Tables/CustomTotal"
import pageListRenderer from "../../components/Tables/PageListRenderer"
import RemotePagination from "../../components/Tables/RemotePagination"
import sizePerPageOptionRenderer from "../../components/Tables/SizePerPageOptionRenderer"
import {
    EMAIL_VERIFICATION_DROPDOWN,
    GLOBAL_CONSTANTS,
    MEMBERSHIP_PLANS,
    MEMBERSHIP_PLANS_LIST
} from "../../constants/constants"
import { countryNamesInEnglish } from "../../utils/commonFunctions"
import ExpandedRow from "./ExpandedRow"

function CustomersTable({
    verifyCustomer,
    downgradeCustomer,
    creditCustomer,
    setShow,
    setCustomer,
    customers,
    countryList,
    customerSearch,
    setCustomerSearch,
    tableDataChange,
    onSearchClick,
    customersDestinations
}: {
    verifyCustomer: Function
    downgradeCustomer: Function
    creditCustomer: Function
    setShow: Function
    setCustomer: Function
    customers: CustomerListModel
    countryList: Record<string, any>[]
    customerSearch: any
    setCustomerSearch: Function
    tableDataChange: Function
    onSearchClick: Function
    customersDestinations: Record<string, any>[]
}) {
    const { t } = useTranslation()

    const formattedCustomers = customers?.customers?.map((c: Record<string, any>) => ({
        ...c,
        email_verified_at: c?.email_verified_at ? 1 : 0
    }))
    function customerDate(cell: string) {
        if (cell) return moment(cell).format("DD MMM yyyy")
    }

    const customerActions = (row: Record<string, any>): string[] => {
        let diff = (new Date(row?.created_at)?.valueOf() - Date.now()) / 1000
        diff /= 60 * 60 * 24 * 7
        let weeks = Math.abs(Math.round(diff))
        let actions: string[] =
            row?.user_addresses?.length > 0
                ? ["Update Address", "Update Customer"]
                : ["Add Address", "Update Customer"]
        //let actions : string [] = []
        actions = row?.email_verified_at ? actions : [...actions, "Verify"]
        if (row?.user_type?.toLowerCase() !== MEMBERSHIP_PLANS.BASIC) {
            actions = [...actions, "Downgrade", "Mark/Unmark as Credited", "Apply PromoCode"]
        }
        if (row?.user_type?.toLowerCase() !== MEMBERSHIP_PLANS.BUSINESS) {
            actions = [...actions, "Upgrade"]
        }
        if (row?.parcel_count === 0 && weeks <= GLOBAL_CONSTANTS.NO_WEEKS) {
            actions = [...actions, "Delete Customer"]
        }
        return actions
    }

    const handleModals = (row: Record<string, any>, modalName: string) => {
        setCustomer(row)
        setShow(modalName)
    }

    const handleTableChange = (type: any, { page, sizePerPage }: { page: number; sizePerPage: number }) => {
        tableDataChange(page, sizePerPage)
    }
    const handleAction = (action: string, row: Record<string, any>) => {
        switch (action) {
            case "Add Address":
                handleModals(row, "updateAddressModal")
                break
            case "Update Address":
                handleModals(row, "updateAddressModal")
                break
            case "Verify":
                verifyCustomer(row?.id)
                break
            case "Downgrade":
                row?.user_type?.toLowerCase() === MEMBERSHIP_PLANS.PREMIUM
                    ? downgradeCustomer(row?.id, MEMBERSHIP_PLANS.B?.toLowerCase())
                    : handleModals(row, "downgradeModal")

                break
            case "Upgrade":
                handleModals(row, "upgradeModal")

                break
            case "Apply PromoCode":
                handleModals(row, "applyPromoCode")

                break
            case "Mark/Unmark as Credited":
                handleModals(row, "creditConfirmModal")
                break

            case "Delete Customer":
                handleModals(row, "deleteConfirmModal")
                break
            case "Update Customer":
                handleModals(row, "customerUpdateModal")
                break

            default:
                break
        }
    }

    const customersColumns = [
        {
            dataField: "id",
            text: t("#")
        },
        {
            text: t("Customer_Id"),
            dataField: "user_pin"
        },
        {
            text: t("Plan_Type"),
            dataField: "user_type",
            classes: "membership_plan",
            formatter: (cell: string, row: Record<string, any>[]) => {
                return cell?.toLowerCase() === "B"?.toLocaleLowerCase() ? (
                    <div className="plan_basic">{t("Basic")}</div>
                ) : cell?.toLowerCase() === "W"?.toLocaleLowerCase() ? (
                    <div className="plan_business">{t("Business")}</div>
                ) : cell?.toLowerCase() === "P"?.toLocaleLowerCase() ? (
                    <div className="plan_premium">{t("Premium")}</div>
                ) : (
                    ""
                )
            }
        },
        {
            text: t("Credited"),
            dataField: "is_credit_user",
            formatter: (cell: boolean) => {
                return cell ? (
                    <div className="status">{t("Yes")}</div>
                ) : (
                    <div className="expired">{t("No")}</div>
                )
            }
        },
        {
            text: t("Email"),
            dataField: "email"
        },
        {
            text: t("Name"),
            dataField: "name"
        },
        {
            text: t("Phone"),
            dataField: "phone"
        },
        {
            text: t("Country"),
            dataField: "country",
            formatter: (cell: string) => {
                return cell && cell !== "" ? (
                    <OverlayTrigger
                        placement="bottom"
                        delay={{ show: 250, hide: 400 }}
                        overlay={<Tooltip id={`${cell}-tooltip`}>{countryNamesInEnglish.of(cell)}</Tooltip>}>
                        <span className="tr-pointer">{cell}&nbsp;</span>
                    </OverlayTrigger>
                ) : (
                    "-"
                )
            }
        },
        {
            text: t("Verified"),
            dataField: "email_verified_at",
            formatter: (cell: string, row: Record<string, any>) => {
                return row?.email_verified_at ? (
                    <span className="status">{t("Verified")}</span>
                ) : (
                    <span className="expired"> {t("Unverified")} </span>
                )
            },
            csvFormatter: (cell: number, row: Record<string, any>[]) => {
                return cell === 1 ? t("Active") : t("Inactive")
            }
        },
        {
            dataField: "",
            text: t("Promo Code"),
            sort: true,
            formatter: (cell: string, row: Record<string, any>) => {
                return row?.PlanTransaction?.promo_code
            },
            csvFormatter: (cell: string, row: Record<string, any>) => {
                return row?.PlanTransaction?.promo_code
            }
        },
        {
            dataField: "created_at",
            text: t("DATE of Sub"),
            sort: true,
            formatter: (cell: string, row: Record<string, any>) => {
                return customerDate(cell)
            },
            csvFormatter: (cell: string, row: Record<string, any>) => {
                return customerDate(cell)
            }
        },
        {
            dataField: "",
            text: t("DATE of Expiry"),
            sort: true,
            formatter: (cell: string, row: Record<string, any>) => {
                return customerDate(row?.PlanTransaction?.expiry_at)
            },
            csvFormatter: (cell: string, row: Record<string, any>) => {
                return customerDate(row?.PlanTransaction?.expiry_at)
            }
        },
        {
            dataField: "",
            text: t("Actions"),
            csvExport: false,
            formatter: (cell: number, row: Record<string, any>) => {
                return customerActions(row)?.length > 0 ? (
                    <Dropdown className="actions-menu">
                        <Dropdown.Toggle variant="primary" id="dropdown-basic" size={"sm"} className="py-0">
                            {t("Actions")}
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            {customerActions(row)?.map((action: string) => (
                                <Dropdown.Item key={action} onClick={() => handleAction(action, row)}>
                                    {action}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                ) : null
            },
            csvFormatter: (cell: string, row: Record<string, any>) => {
                return null
            }
        }
    ]

    const expandRow = {
        renderer: (row: any): JSX.Element => {
            const addressData =
                row?.user_addresses &&
                row?.user_addresses.filter((data: Record<string, any>) => data.is_primary === 1)
            const address = addressData?.length > 0 ? addressData[addressData.length - 1] : ""

            return <ExpandedRow row={row} address={address} countryList={countryList} />
        },
        showExpandColumn: true,
        expandByColumnOnly: true,
        expandHeaderColumnRenderer: ({ isAnyExpands }: { isAnyExpands: boolean }) => {
            return <></>
        },
        expandColumnRenderer: ({ expanded }: { expanded: boolean }) => {
            if (expanded) {
                return <ChevronUp color="#2D3748" size={10} className="mx-2 my-3 arrow-icon" />
            }
            return <ChevronDown color="#2D3748" size={10} className="mx-2 my-3 arrow-icon" />
        }
    }

    const options = {
        sizePerPage: 50,
        hideSizePerPage: false,
        hidePageListOnlyOnePage: true,
        sizePerPageOptionRenderer,
        showTotal: true,
        pageListRenderer,
        paginationTotalRenderer: CustomTotal
    }

    const { ExportCSVButton } = CSVExport

    return (
        <div className="table-responsive">
            {customersColumns && (
                <ToolkitProvider
                    keyField="user_pin"
                    data={formattedCustomers}
                    columns={customersColumns as ColumnDescription<any, any>[]}
                    search={{ searchFormatted: true }}
                    exportCSV={{
                        fileName: "CustomersTable.csv",
                        noAutoBOM: false,
                        exportAll: false,
                        onlyExportFiltered: true
                    }}>
                    {(props) => (
                        <div className="table-container my-4">
                            <Row className="search customers-search">
                                <Col xs={12} sm={12} md={12} lg={9}>
                                    <Row>
                                        <Col xs={12} sm={6} md={6} lg={3} className="customer-select-column">
                                            <div id="filter-div">
                                                <select
                                                    id="plan_type"
                                                    name="plan_type"
                                                    value={customerSearch?.plan_type}
                                                    onChange={(e: any) =>
                                                        setCustomerSearch({
                                                            ...customerSearch,
                                                            [e.target.name]: e.target.value
                                                        })
                                                    }
                                                    className="form-control select-column col-lg-3 col-md-6 col-sm-12 col-12 w-100 input-lg">
                                                    {MEMBERSHIP_PLANS_LIST?.map((plan: any) => (
                                                        <option
                                                            value={plan.value}
                                                            id={`plan-${plan.value}`}
                                                            selected={plan.value === customerSearch?.plan_type}>
                                                            {plan.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </Col>
                                        <Col xs={12} sm={6} md={6} lg={3}>
                                            <div id="filter-div">
                                                <select
                                                    id="country"
                                                    name="country"
                                                    onChange={(e: any) =>
                                                        setCustomerSearch({
                                                            ...customerSearch,
                                                            [e.target.name]: e.target.value
                                                        })
                                                    }
                                                    className="form-control select-column col-lg-3 col-md-6 col-sm-12 col-12 w-100 input-lg">
                                                    <option value="">Country: All</option>
                                                    {customersDestinations?.map((destinations: any) => (
                                                        <option
                                                            key={destinations?.country}
                                                            selected={
                                                                destinations?.country ===
                                                                customerSearch?.country
                                                            }
                                                            value={destinations?.country}>
                                                            {"Country: " + destinations?.country}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </Col>
                                        <Col xs={12} sm={6} md={6} lg={3}>
                                            <div id="filter-div">
                                                <select
                                                    id="email_verification"
                                                    name="email_verification"
                                                    value={customerSearch?.email_verification}
                                                    onChange={(e: any) =>
                                                        setCustomerSearch({
                                                            ...customerSearch,
                                                            [e.target.name]: e.target.value
                                                        })
                                                    }
                                                    className="form-control select-column col-lg-3 col-md-6 col-sm-12 col-12 w-100 input-lg">
                                                    {EMAIL_VERIFICATION_DROPDOWN?.map((email: any) => (
                                                        <option
                                                            value={email.value}
                                                            id={`email-${email.value}`}
                                                            selected={
                                                                email.value ===
                                                                customerSearch?.email_verification
                                                            }>
                                                            {email.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </Col>
                                        <Col xs={12} sm={6} md={6} lg={3}>
                                            <input
                                                className="form-control"
                                                type="text"
                                                placeholder={"Search By CUSTOMER ID / Name / Email / Phone"}
                                                name="customer"
                                                value={customerSearch?.customer}
                                                onChange={(e) =>
                                                    setCustomerSearch({
                                                        ...customerSearch,
                                                        [e.target.name]: e.target.value
                                                    })
                                                }
                                            />
                                        </Col>
                                        <Col xs={12} sm={6} md={6} lg={3}></Col>
                                    </Row>
                                </Col>
                                <Col xs={12} sm={12} md={12} lg={3} className="d-flex justify-content-end">
                                    <Button
                                        id="btn-primary"
                                        className="btn btn-primary w-100"
                                        type="submit"
                                        onClick={(e: any) => onSearchClick()}>
                                        {t("Search")}
                                    </Button>
                                </Col>
                                <Col className="d-none">
                                    <ExportCSVButton {...props.csvProps} className="customersExportButton">
                                        Export
                                    </ExportCSVButton>
                                </Col>
                            </Row>
                            <Row className="table-data my-3">
                                <Col>
                                    {customers?.customers?.length > 0 && (
                                        <RemotePagination
                                            data={formattedCustomers}
                                            page={customers?.page}
                                            sizePerPage={customers?.page_size}
                                            totalSize={customers?.total}
                                            onTableChange={handleTableChange}
                                            columns={customersColumns}
                                            keyField="uuid"
                                            expandRow={expandRow}
                                            props={props}
                                        />
                                    )}
                                </Col>
                            </Row>
                        </div>
                    )}
                </ToolkitProvider>
            )}
        </div>
    )
}

export default CustomersTable
